import React, { useEffect, useRef, useState, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import {
  fetchEvaluationQuestion,
  saveQuestionEvaluation,
  submitTaskEvaluation,
} from "@services/evaluationService";
import type {EvaluationTaskResponse} from "@services/evaluationService";
import styles from "./EvaluatorPage.module.css";
import ConfirmationDialog from "@modals/confirmationdialog/ConfirmationDialog";
import { setComponentToShow } from "@store/ModalToShow";
import { useNavigate } from "react-router-dom";
import ReturnToDashboard from "@modals/returntodashboard/ReturnToDashboard";
import QuestionWise from "./QuestionWise";
import type { EvaluationFormData } from "./QuestionWise";
import AnswersheetWise from "./AnswersheetWise";
import { EVALUATION_TYPES } from "@constants/evaluationConstants";
import { getExamSessionDetailsById } from "@services/adminDashboard";

function EvaluatorPage() {
  const [currentEvaluationTask, setCurrentEvaluationTask] = useState<EvaluationTaskResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [evaluationData, setEvaluationData] = useState<EvaluationFormData>({
    marksAwarded: 0,
    feedback: "",
    verdict:""
  });
  const [sessionDetails,setSessionDetails]=useState<any>(null)
  const effectRun = useRef(false);
  const dispatch = useDispatch();
  const navigate=useNavigate();
  const accesstoken = localStorage.getItem("accessToken");
  const evaluationMode = currentEvaluationTask?.evaluation_type;
  const [searchParams]=useSearchParams();
  const examSessionId=searchParams?.get("exam_session_id");

  useEffect(() => {
    if (localStorage.getItem("showConfirmationDialog") === null) {
      localStorage.setItem("showConfirmationDialog", "true");
    }
  }, []);

  useEffect(() => {
    if (!effectRun.current) {
      effectRun.current = true;
      loadNextQuestion();
      if(examSessionId)
      {
        loadSession(examSessionId);
      }
    }
  }, []);

  async function loadSession(examSessionId:string| null) {
    if(!examSessionId) return;
    try {
      const sessiondata = await getExamSessionDetailsById(examSessionId);
      setSessionDetails(sessiondata);
    } catch (error) {
      console.error("Failed to load session:", error);
    }
  }

  const componentName = useSelector(
    (state: any) => state.modalToShow.componentName
  );

  const renderComponent = () => {
    switch (componentName) {
      case "ReturnToDashboard":
        return (
          <ReturnToDashboard
            isOpen={true}
            onClose={() => {
              dispatch(setComponentToShow(null));
            }}
          />
        );
      default:
        return null;
    }
  };

  const loadNextQuestion = async () => {
    setLoading(true);
    try {
      const questionData = await fetchEvaluationQuestion(accesstoken,examSessionId);
      setCurrentEvaluationTask(questionData);
    } catch (err) {
      console.error("Error fetching evaluation question:", err);
      // Fallback to mock data for development/testing
    }
    setLoading(false);
  };

  const proceedWithSubmission = async () => {
    try {
      if (!currentEvaluationTask || !currentEvaluationTask.questions?.[0]) {
        throw new Error("No evaluation task or question available");
      }
      
      const questionWithEvaluation = currentEvaluationTask.questions[0];
      const feedbacks = evaluationData.feedback ? [evaluationData.feedback] : [];
      console.log(questionWithEvaluation.question.max_marks)
      setIsSaving(true);
      // Step 1: Save the question evaluation
      try{
      await saveQuestionEvaluation({
        taskId: currentEvaluationTask.task_id,
        questionId: questionWithEvaluation.question.id,
        awardedMarks: evaluationData.marksAwarded,
        feedbacks: feedbacks,
        verdict:evaluationData.verdict,
        token: accesstoken ?? "",
      });
    }catch(err){
      console.error("Error saving question evaluation:", err);
    }finally{
      setIsSaving(false);
    }

      console.log("Question evaluation saved successfully");
      
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : String(err);
      alert("Error saving evaluation: " + errorMessage);
      console.error("Error in proceedWithSubmission:", err);
      throw err; // Re-throw to handle in calling functions
    }
  };

  const proceedWithTaskSubmission = async () => {
    setIsSaving(true);
    try {
      if (!currentEvaluationTask) {
        throw new Error("No evaluation task available");
      }

      // Step 2: Submit the entire task
      await submitTaskEvaluation({
        taskId: currentEvaluationTask.task_id,
        token: accesstoken ?? "",
      });

      console.log("Task evaluation submitted successfully");
      
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : String(err);
      alert("Error submitting task: " + errorMessage);
      console.error("Error in proceedWithTaskSubmission:", err);
      throw err;
    }finally{
      setIsSaving(false);
    }
  };

  const handleDialogConfirm = async () => {
    try {
      await proceedWithSubmission();
      await proceedWithTaskSubmission();
      await loadNextQuestion();
      setShowDialog(false);
    } catch (err) {
      // Error already handled in the individual functions
      setShowDialog(false);
    }
  };

  const handleDialogCancel = () => {
    setShowDialog(false);
  };

  const handleDontShowAgain = (checked: boolean) => {
    if (checked) {
      localStorage.setItem("showConfirmationDialog", "false");
    } else {
      localStorage.setItem("showConfirmationDialog", "true");
    }
  };

  const handleApproveAndNext = async () => {
    const showConfirmationDialog =
      localStorage.getItem("showConfirmationDialog") === "true";

    if (!currentEvaluationTask) return;

    if (showConfirmationDialog) {
      setShowDialog(true);
      return;
    }
    
    try {
      await proceedWithSubmission();
      await proceedWithTaskSubmission();
      await loadNextQuestion();
    } catch (err) {
      // Error already handled in the individual functions
    }
  };


  const handleApproveAndSubmit = async () => {
    if (!currentEvaluationTask) return;
    
    try {
      await proceedWithSubmission();
      await proceedWithTaskSubmission();
      navigate("/evaluator-dashboard");
    } catch (err) {
      // Error already handled in the individual functions
      // Don't redirect if there was an error
    }
  };

  const handleEvaluationDataChange = useCallback((data: EvaluationFormData) => {
    setEvaluationData(data);
  }, []);

  const handleAssignNextTask = async () => {
    if (!currentEvaluationTask) return;
    
    try {
      await proceedWithTaskSubmission(); 
  
      // 2. Only if the above succeed, we start loading the next task
      setLoading(true);
      const questionData = await fetchEvaluationQuestion(accesstoken, examSessionId);
      
      if (questionData && questionData.task_id) {
        // Success: Reset state with new data
        setCurrentEvaluationTask(questionData);
        dispatch(setComponentToShow(null)); 
      } else {
        alert("No more tasks available.");
        navigate("/evaluator-dashboard");
      }
    } catch (err) {
      console.error("Error assigning next task:", err);
      navigate("/evaluator-dashboard");
    } finally {
      setLoading(false);
    }
  };

  function TaskNotFound() {
    return <div>Task not found</div>;
  }

  if (loading) return <div>Loading...</div>;

    return (
      <>
        {currentEvaluationTask ? (
          <>
            {evaluationMode === EVALUATION_TYPES.QUESTION_WISE_EVALUATION ? (
              <QuestionWise
                examSessionData={currentEvaluationTask}
                sessionName={sessionDetails?.name ?? ""}
                onApproveAndNext={handleApproveAndNext}
                onApproveAndSubmit={handleApproveAndSubmit}
                onEvaluationDataChange={handleEvaluationDataChange}
                isSaving={isSaving}
              />
            ) : (
              <AnswersheetWise
                examSessionData={currentEvaluationTask}
                sessionName={sessionDetails?.name ?? ""}
                handleAssignNextTask={handleAssignNextTask}
                onEvaluationDataChange={handleEvaluationDataChange}
              />
            )}
    
            {showDialog && (
              <ConfirmationDialog
                show={showDialog}
                onConfirm={handleDialogConfirm}
                onCancel={handleDialogCancel}
                onDontShowAgain={handleDontShowAgain}
                isSaving={isSaving}
              />
            )}
    
            {renderComponent()}
          </>
        ) : (
          <TaskNotFound />
        )}
      </>
    );
}

export default EvaluatorPage;